/**
 * RestoID — the consent screen's permission list.
 * ---------------------------------------------------------------------------
 * Replaces `src/pages/Consent/ScopesListCard` + `src/pages/Consent/ScopeGroup`
 * with the design reference's treatment (`ConsentScreen`): one bordered card,
 * every granted scope on its own row behind a lime check, groups separated by a
 * hairline and labelled with a small caption.
 *
 * The data mapping is upstream's, line for line:
 *   - a scope that is a known `UserScope` is described with
 *     `user_scopes.descriptions.<scope>` and the SAME `nsSeparator: '|'` escape —
 *     the scope names contain ':' (`urn:logto:scope:organizations`), which i18next
 *     would otherwise read as a namespace separator and render as a raw key;
 *   - a scope that is not a known `UserScope` (a tenant's own OIDC scope) gets no
 *     description and falls back to its raw name, exactly as upstream's
 *     `description ?? name`;
 *   - resource scopes keep their own `description ?? name` fallback, and a group
 *     per resource, named after the resource;
 *   - the whole block still disappears when there is nothing to grant, so the
 *     title never dangles over an empty card.
 *
 * Two deliberate differences:
 *   - nothing collapses. Upstream renders each group as a click-to-expand row and
 *     spends `isAutoExpand` heuristics deciding which one starts open; a consent
 *     screen that hides what it is about to grant behind a chevron is the wrong
 *     default, and with the collapse gone the heuristics have nothing left to
 *     decide. Strictly more is visible than before.
 *   - the user-scope group is labelled `description.user_scopes` ("Личные данные
 *     пользователя") instead of upstream's hard-coded English literal
 *     "User Scopes", which no locale could ever translate.
 */
import { UserScope } from '@logto/core-kit';
import { type ConsentInfoResponse } from '@logto/schemas';
import { type Nullable } from '@silverhand/essentials';
import { type ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { C, FONT, Icon } from './restoid';

export type ConsentScopeGroup = {
  /** React key — a resource id, or a fixed slug for the user-scope group. */
  key: string;
  name: ReactNode;
  scopes: Array<{
    id: string;
    name: string;
    /** Organization scope descriptions can be `null` (upstream's own note). */
    description?: Nullable<string>;
  }>;
};

type GroupsProps = {
  /** Optional lead-in above the card; omitted for the organization's own scopes. */
  title?: ReactNode;
  groups: ConsentScopeGroup[];
};

/** The bordered scope card itself — shared with the organization selector. */
export const ConsentScopeGroups = ({ title, groups }: GroupsProps) => {
  if (groups.length === 0) {
    return null;
  }

  return (
    <div>
      {title ? (
        <p style={{ margin: '0 0 10px', color: C.sub, font: `400 14px/1.5 ${FONT}` }}>{title}</p>
      ) : null}
      <div
        style={{
          borderRadius: 14,
          background: C.card,
          border: `1px solid ${C.lineSoft}`,
          overflow: 'hidden',
        }}
      >
        {groups.map((group, index) => (
          <div
            key={group.key}
            style={{
              padding: '13px 15px',
              borderTop: index === 0 ? undefined : `1px solid ${C.lineSoft}`,
            }}
          >
            <div
              style={{
                marginBottom: 9,
                color: C.faint,
                font: `500 11px/1 ${FONT}`,
                letterSpacing: '0.07em',
                textTransform: 'uppercase',
              }}
            >
              {group.name}
            </div>
            <ul
              style={{
                margin: 0,
                padding: 0,
                listStyle: 'none',
                display: 'flex',
                flexDirection: 'column',
                gap: 9,
              }}
            >
              {group.scopes.map(({ id, name, description }) => (
                <li key={id} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                  <span
                    style={{
                      flex: '0 0 auto',
                      display: 'inline-flex',
                      marginTop: 1,
                      color: C.brand,
                    }}
                  >
                    <Icon name="check" size={16} />
                  </span>
                  <span style={{ color: C.text, font: `400 14px/1.45 ${FONT}` }}>
                    {description ?? name}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
};

const isUserScope = (scope: string): scope is UserScope =>
  Object.values<string>(UserScope).includes(scope);

type Props = {
  readonly userScopes: ConsentInfoResponse['missingOIDCScope'];
  readonly resourceScopes: ConsentInfoResponse['missingResourceScopes'];
  readonly appName: string;
};

/** Personal data + API resources the application is asking for. */
export const ConsentScopeList = ({ userScopes, resourceScopes, appName }: Props) => {
  const { t } = useTranslation();

  const userScopesData = useMemo(
    () =>
      userScopes?.map((scope) => ({
        id: scope,
        name: scope,
        description: isUserScope(scope)
          ? // We have ':' in the user scope, need to change the nsSeparator to '|'
            // to avoid i18n ns matching (upstream ScopesListCard, verbatim).
            t(`user_scopes.descriptions.${scope}`, { nsSeparator: '|' })
          : undefined,
      })),
    [t, userScopes]
  );

  // If there is no user scopes and resource scopes, we don't need to show the list.
  if (!userScopesData?.length && !resourceScopes?.length) {
    return null;
  }

  const groups: ConsentScopeGroup[] = [
    ...(userScopesData && userScopesData.length > 0
      ? [{ key: 'user-scopes', name: t('description.user_scopes'), scopes: userScopesData }]
      : []),
    ...(resourceScopes ?? []).map(({ resource, scopes }) => ({
      key: resource.id,
      name: resource.name,
      scopes,
    })),
  ];

  return (
    <ConsentScopeGroups
      title={t('description.request_permission', { name: appName })}
      groups={groups}
    />
  );
};
